export type LogicValue = string | number | boolean | null | LogicValue[] | {[key: string]: LogicValue}

type Token = {kind: "word" | "string" | "number" | "symbol" | "end", value: string, at: number}
type Value = {kind: "literal", value: LogicValue} | {kind: "access", path: (string | number)[]}
type Expression = Value | {kind: "equal", left: Value, right: Value} | {kind: "not", value: Expression} | {kind: "and", left: Expression, right: Expression} | {kind: "or", left: Expression, right: Expression}

function tokens(source: string): Token[] {
  if (source.length > 4096) throw new Error("Restriction is too long")
  const result: Token[] = []
  let at = 0
  while (at < source.length) {
    if (/\s/.test(source[at]!)) { at++; continue }
    const start = at
    const char = source[at]!
    if ("[]()=".includes(char)) { result.push({kind: "symbol", value: char, at: at++}); continue }
    if (char === '"') {
      at++
      while (at < source.length && source[at] !== '"') {
        if (source[at] === "\\") at++
        at++
      }
      if (at >= source.length) throw new Error(`Unterminated string at ${start}`)
      at++
      const raw = source.slice(start, at)
      let value: string
      try { value = JSON.parse(raw) } catch { throw new Error(`Invalid string at ${start}`) }
      result.push({kind: "string", value, at: start})
      continue
    }
    if (/[0-9-]/.test(char)) {
      const match = source.slice(at).match(/^-?(?:0|[1-9][0-9]*)(?:\.[0-9]+)?/)
      if (!match) throw new Error(`Invalid number at ${start}`)
      at += match[0].length
      result.push({kind: "number", value: match[0], at: start})
      continue
    }
    const match = source.slice(at).match(/^[A-Za-z_][A-Za-z0-9_]*/)
    if (!match) throw new Error(`Unexpected character ${JSON.stringify(char)} at ${start}`)
    at += match[0].length
    result.push({kind: "word", value: match[0], at: start})
  }
  result.push({kind: "end", value: "", at})
  return result
}

export function compileLogic(source: string): (scope: {step: LogicValue}) => boolean {
  const input = tokens(source)
  let at = 0
  const peek = () => input[at]!
  const take = (value?: string) => {
    const token = peek()
    if (value !== undefined && token.value !== value) throw new Error(`Expected ${value} at ${token.at}`)
    at++
    return token
  }
  const word = (value: string) => peek().kind === "word" && peek().value.toUpperCase() === value

  function parseValue(): Value {
    const token = take()
    if (token.kind === "string") return {kind: "literal", value: token.value}
    if (token.kind === "number") return {kind: "literal", value: Number(token.value)}
    if (token.kind === "word" && ["true", "false", "null"].includes(token.value)) {
      return {kind: "literal", value: token.value === "null" ? null : token.value === "true"}
    }
    if (token.kind !== "word" || token.value !== "step") throw new Error(`Expected a value at ${token.at}`)
    const path: (string | number)[] = []
    while (peek().value === "[") {
      take("[")
      const part = take()
      if (part.kind !== "string" && part.kind !== "number") throw new Error(`Expected property or index at ${part.at}`)
      path.push(part.kind === "number" ? Number(part.value) : part.value)
      take("]")
    }
    return {kind: "access", path}
  }

  function primary(): Expression {
    if (peek().value === "(") { take("("); const value = or(); take(")"); return value }
    const left = parseValue()
    if (peek().value !== "=") return left
    take("=")
    return {kind: "equal", left, right: parseValue()}
  }
  function unary(): Expression { return word("NOT") ? (take(), {kind: "not", value: unary()}) : primary() }
  function and(): Expression {
    let value = unary()
    while (word("AND")) { take(); value = {kind: "and", left: value, right: unary()} }
    return value
  }
  function or(): Expression {
    let value = and()
    while (word("OR")) { take(); value = {kind: "or", left: value, right: and()} }
    return value
  }

  const expression = or()
  if (peek().kind !== "end") throw new Error(`Unexpected token ${peek().value} at ${peek().at}`)

  return scope => {
    let fuel = 10_000
    const spend = () => { if (--fuel < 0) throw new Error("Restriction exceeded its fuel limit") }
    const value = (item: Value): LogicValue => {
      spend()
      if (item.kind === "literal") return item.value
      let result: LogicValue = scope.step
      for (const part of item.path) {
        if (result === null || typeof result !== "object" || !Object.hasOwn(result, part)) return null
        result = (result as any)[part] ?? null
      }
      return result
    }
    const evaluate = (item: Expression): boolean => {
      spend()
      if (item.kind === "equal") return Object.is(value(item.left), value(item.right))
      if (item.kind === "not") return !evaluate(item.value)
      if (item.kind === "and") return evaluate(item.left) && evaluate(item.right)
      if (item.kind === "or") return evaluate(item.left) || evaluate(item.right)
      return value(item) === true
    }
    return evaluate(expression)
  }
}
